import {
    confirmResetPassword,
    confirmSignIn,
    confirmSignUp,
    fetchAuthSession,
    resendSignUpCode,
    signIn,
    signInWithRedirect,
    signOut,
    updatePassword as updatePasswordCognito,
} from '@aws-amplify/auth';
import { Amplify } from 'aws-amplify';
import { Hub } from 'aws-amplify/utils';
import axios from 'axios';
import { useSnackbar } from 'contexts/snackbar';
import jwt from 'jsonwebtoken';
import queryString from 'query-string';
import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import accountRequests from 'services/account';
import authRequests from 'services/auth';
import courseRequests from 'services/course';
import userRequests from 'services/user';
import { ROLES } from 'utils/constants';
import { v4 as uuid } from 'uuid';

export const launchUri = (url) => {
    const windowProxy = window.open(url, '_self');
    if (windowProxy) {
        return Promise.resolve(windowProxy);
    } else {
        return Promise.reject();
    }
};
let platform = process.env.REACT_APP_COGNITO_SUBDOMAIN;

// Conditional behavior based on the page
if (window.location.pathname === '/forgot-password') {
    // Bypass OAuth redirect logic
    Amplify.configure({
        Auth: {
            // REQUIRED only for Federated Authentication - Amazon Cognito Identity Pool ID
            // identityPoolId: 'XX-XXXX-X:XXXXXXXX-XXXX-1234-abcd-1234567890ab',

            // REQUIRED - Amazon Cognito Region
            region: process.env.REACT_APP_AWS_REGION,

            // OPTIONAL - Amazon Cognito Federated Identity Pool Region
            // Required only if it's different from Amazon Cognito Region
            // identityPoolRegion: 'XX-XXXX-X',
            Cognito: {
                // OPTIONAL - Amazon Cognito User Pool ID
                userPoolId: process.env.REACT_APP_USERPOOL_ID,

                // OPTIONAL - Amazon Cognito Web Client ID (26-char alphanumeric string)
                userPoolClientId: process.env.REACT_APP_CLIENT_ID,

                // OPTIONAL - Enforce user authentication prior to accessing AWS resources or not
                mandatorySignIn: false,
                loginWith: {
                    oauth: {
                        redirectSignIn: '',
                    },
                },
            },
        },
    });

    // Handle password reset logic
} else {
    // manual setup
    Amplify.configure({
        Auth: {
            // REQUIRED only for Federated Authentication - Amazon Cognito Identity Pool ID
            // identityPoolId: 'XX-XXXX-X:XXXXXXXX-XXXX-1234-abcd-1234567890ab',

            // REQUIRED - Amazon Cognito Region
            region: process.env.REACT_APP_AWS_REGION,

            // OPTIONAL - Amazon Cognito Federated Identity Pool Region
            // Required only if it's different from Amazon Cognito Region
            // identityPoolRegion: 'XX-XXXX-X',

            Cognito: {
                userPoolId: process.env.REACT_APP_USERPOOL_ID,
                userPoolClientId: process.env.REACT_APP_CLIENT_ID,
                // OPTIONAL - Enforce user authentication prior to accessing AWS resources or not
                mandatorySignIn: false,
                loginWith: {
                    oauth: {
                        domain: process.env.REACT_APP_COGNITO_DOMAIN,
                        scopes: ['email', 'profile', 'openid', 'aws.cognito.signin.user.admin'],

                        redirectSignOut:
                            window.location.hostname === 'localhost'
                                ? [`http://localhost:8000`]
                                : [`https://${platform}.timelygrader.ai`],
                        redirectSignIn:
                            window.location.hostname === 'localhost'
                                ? [`http://localhost:8000/federated-login`]
                                : [`https://${platform}.timelygrader.ai/federated-login`],
                        responseType: 'code', // for Authorization code grant
                    },
                },
            },
        },
    });
}

const AuthContext = createContext();
const useAuth = () => useContext(AuthContext);

const AuthProvider = (props) => {
    const { message } = useSnackbar();
    const history = useHistory();
    const location = useLocation();

    const [auth, setAuth] = useState();
    const [currentUser, setCurrentUser] = useState();
    const [loading, setLoading] = useState(true);

    const updateCurrentUser = async (data) => {
        const updateUser = await userRequests.updateUser(auth.user_id, data);
        setCurrentUser(updateUser);
        return updateUser;
    };

    const logout = useCallback(
        async ({ isExpired = false }) => {
            localStorage.removeItem('auth_token');
            axios.defaults.headers.common.Authorization = undefined;
            await signOut({
                global: false,
                oauth: {
                    redirects: `https://${platform}.timelygrader.ai`,
                    redirectUrl: `https://${platform}.timelygrader.ai`,
                },
            });
            setAuth(undefined);
            if (isExpired) {
                setLoading(false);
                return history.push('/', { isExpired });
            }
            return history.push('/');
        },
        [history]
    );

    const verifyUser = useCallback(
        async (username, code) => {
            try {
                await confirmSignUp({ username, confirmationCode: code });
                return true;
            } catch (e) {
                // eslint-disable-next-line no-console
                console.log(e.toString());
                const findConfirmed = e.toString().includes('CONFIRMED');
                const findError = e.toString().includes('ExpiredCodeException');
                if (findConfirmed) {
                    return true;
                }
                if (findError) {
                    message.error('Something went wrong. Please try again later.');
                    await logout({ isExpired: false });
                    throw new Error();
                }

                return await logout({ isExpired: false });
            }
        },
        [logout, message]
    );

    const login = useCallback(
        async ({ username = '', password = '', confirmation_code = null }) => {
            try {
                let nextCognitoStep = null;
                const trimmedUsername = username?.toLowerCase()?.trim();
                const { nextStep } = await signIn({ username: trimmedUsername, password });
                nextCognitoStep = nextStep.signInStep;
                if (nextCognitoStep === 'CONFIRM_SIGN_UP') {
                    await verifyUser(trimmedUsername, confirmation_code);
                    const { nextStep } = await signIn({ username: trimmedUsername, password });
                    nextCognitoStep = nextStep;
                }
                if (nextCognitoStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
                    await confirmSignIn({ challengeResponse: password });
                }

                const currentUserData = await fetchAuthSession();
                if (!currentUserData) throw new Error();
                const res = await authRequests.signin({
                    username: trimmedUsername,
                    cognito_user_id: currentUserData.userSub,
                });
                const { auth_token: token } = res;
                if (!res) return new Error();
                localStorage.setItem('auth_token', token);
                axios.defaults.headers.common.Authorization = `Bearer ${token}`;
                const cachedUser = jwt.decode(token);
                const userData = await userRequests.getUser(cachedUser.user_id);
                if (cachedUser.owner_account_id) {
                    const accountData = await accountRequests.findAccount(cachedUser.owner_account_id);
                    setAuth({ ...cachedUser, ...accountData, cognito_user_id: currentUserData.userSub });
                } else {
                    setAuth({ ...cachedUser, cognito_user_id: currentUserData.userSub });
                }
                setCurrentUser({ ...res, ...userData, cognito_user_id: currentUserData.userSub });

                if (userData.admin === 'true') return 'admin';
                else if (!userData?.profile_set_up) {
                    return 'profile-set-up';
                } else {
                    return 'login';
                }
            } catch (e) {
                console.log(e);
                throw new Error();
            }
        },
        [verifyUser]
    );

    const loginWithThirdParty = useCallback(
        async (email, cognitoUserId) => {
            try {
                setLoading(true);
                const res = await authRequests.signin({
                    username: email,
                    cognito_user_id: cognitoUserId,
                });
                const { auth_token: token } = res;
                if (!res) return new Error();
                localStorage.setItem('auth_token', token);
                axios.defaults.headers.common.Authorization = `Bearer ${token}`;
                const cachedUser = jwt.decode(token);
                const userData = await userRequests.getUser(cachedUser.user_id);
                const user = await fetchAuthSession();

                if (cachedUser.owner_account_id) {
                    const accountData = await accountRequests.findAccount(cachedUser.owner_account_id);
                    setAuth({
                        ...cachedUser,
                        ...userData,
                        ...accountData,
                        cognito_user_id: user.userSub,
                        is_third_party: true,
                    });
                } else {
                    setAuth({ ...cachedUser, ...userData, is_third_party: true, cognito_user_id: user.userSub });
                }
                setCurrentUser({ ...res, ...userData, is_third_party: true, cognito_user_id: user.userSub });

                if (res.admin === 'true') return 'admin';
                else if (
                    !res?.profile_set_up &&
                    user.tokens.idToken.payload['identities']?.[0]?.providerName !== 'google'
                ) {
                    return history.push('/account-settings');
                } else if (!res?.profile_set_up) {
                    return history.push('/profile-setup');
                } else {
                    setTimeout(() => {
                        history.push('/');
                    }, 2000);
                }
            } catch (e) {
                console.log(e);
                throw new Error();
            }
        },
        [history]
    );

    const sessionExpired = useCallback(() => {
        return logout({ isExpired: true });
    }, [logout]);

    const selectAccount = async (accountId) => {
        setLoading(true);
        const res = await authRequests.selectAccount({ owner_account_id: accountId });
        const { auth_token: token } = res;
        localStorage.setItem('auth_token', token);
        axios.defaults.headers.common.Authorization = `Bearer ${token}`;
        const cachedUser = jwt.decode(token);
        const accountData = await accountRequests.findAccount(cachedUser.owner_account_id);
        const courseData = await courseRequests.findCourse(accountData.course_id);
        const isThirtdParty = await getCurrentUserStatus();
        setAuth({ ...cachedUser, ...accountData, is_third_party: isThirtdParty });
        if (
            !courseData?.finished_course_set_up &&
            accountData?.role !== 'student' &&
            courseData?.created_on > new Date('2021-12-01').getTime()
        ) {
            history.push('/course-set-up');
        } else if (accountData?.role === 'instructor' || accountData?.role === 'student') {
            history.push('/assignments');
        } else {
            history.push('/dashboard');
        }
        setLoading(false);
    };

    const startCourseSetup = async (originalCourseObj) => {
        setLoading(true);
        setAuth((current) => {
            return { iat: current.iat, user_id: current.user_id, username: current.username };
        });

        const newCourseId = uuid();
        let newCourseData = {
            id: newCourseId,
            school: '',
            name: '',
            faculty: '',
            semester: '',
            section: '',
            finished_course_set_up: false,
        };
        if (originalCourseObj) {
            const { name, faculty, field_of_study, school, semester, section } = originalCourseObj;
            newCourseData = {
                ...newCourseData,
                name: name,
                school,
                faculty,
                field_of_study,
                semester,
                section,
                original_course_id: originalCourseObj.course_id,
            };
            history.push({
                pathname: '/course-set-up',
                state: { duplicatingCourse: newCourseData },
            });
        } else {
            history.push('/course-set-up');
        }

        setLoading(false);
        return newCourseData;
    };

    const confirmCourseSetUp = async (newCourseData) => {
        const accountId = uuid();
        await accountRequests.createAccount(accountId, {
            course_id: newCourseData.course_id,
            userData: { username: auth.username, role: ROLES.INSTRUCTOR },
        });
        await courseRequests.createCourse(newCourseData.course_id, newCourseData);
    };

    const resendConfirmationCode = async (username) => {
        try {
            await resendSignUpCode({ username });
            message.success('The verification email was re-sent successfully.');
        } catch (err) {
            console.log('error resending code: ', err);
        }
    };

    const updatePassword = async (oldPassword, newPassword) => {
        return await fetchAuthSession()
            .then(async (user) => {
                if (!newPassword) return new Error();
                await updatePasswordCognito({
                    oldPassword: oldPassword || currentUser.password,
                    newPassword,
                });
                return true;
            })
            .catch((e) => {
                console.log(e);
                return false;
            });
    };

    const register = async ({ username, password }) => {
        try {
            const basename = process.env.REACT_APP_BASE_NAME;
            const trimmedUsername = username?.toLowerCase()?.trim();
            const signUpData = {
                display_name: trimmedUsername,
                name: trimmedUsername,
                first_name: trimmedUsername,
                username: trimmedUsername,
                email: trimmedUsername,
                password,
                admin: false,
                base_url: window.location.origin + basename,
            };
            const createUser = await authRequests.signUp(signUpData);
            message.success('The verification email was sent successfully.');
            return createUser;
        } catch (e) {
            // eslint-disable-next-line no-console
            console.log(e);
            return e.data;
        }
    };

    const forgotPassword = async (email) => {
        try {
            const findUser = await authRequests.forgotPassword({ username: email });
            return findUser;
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error(e);
        }
    };

    const confirmForgotPassword = async (email, code, newPassword) => {
        try {
            const forgotPasswordConfirmReq = await confirmResetPassword({
                username: email?.toLowerCase()?.trim(),
                newPassword,
                confirmationCode: code,
            });

            return forgotPasswordConfirmReq;
        } catch (e) {
            console.log(e);
            return false;
        }
    };

    const getCurrentUser = () => currentUser || null;

    const updateNotificationPreferences = async (newNotification) => {
        try {
            setAuth((auth) => ({
                ...auth,
                notification: newNotification,
            }));
            await accountRequests.updateAccount(auth.owner_account_id, { notification: newNotification });
        } catch (e) {
            console.error(e);
        }
    };

    const updateProfile = async (data) => {
        const prevUser = { ...currentUser };
        const updatedUser = {
            ...currentUser,
            ...data,
        };
        setCurrentUser(updatedUser);

        try {
            await userRequests.updateUser(currentUser.id, data);
        } catch (e) {
            setCurrentUser(prevUser);
        }
    };

    const changeAccount = async () => {
        setLoading(true);
        const res = await authRequests.unselectAccount();
        const { auth_token: token } = res;
        localStorage.setItem('auth_token', token);
        axios.defaults.headers.common.Authorization = `Bearer ${token}`;
        setAuth((current) => ({ iat: current.iat, user_id: current.user_id, username: current.username }));
        setLoading(false);
        return history.push('/');
    };

    const signInWithGoogle = async () => {
        try {
            setLoading(true);
            await signInWithRedirect({
                provider: 'Google',
            });
        } catch (e) {
            console.log(e);
        }
    };

    const getCurrentUserStatus = async () => {
        const session = await fetchAuthSession();
        if (session?.idToken?.payload?.['cognito:username']?.includes('google')) {
            return true;
        }
        return false;
    };

    useEffect(() => {
        const unsubscribe = Hub.listen('auth', async ({ payload: { event, data } }) => {
            switch (event) {
                case 'signInWithRedirect': {
                    break;
                }
                case 'signedIn': {
                    setLoading(true);
                    const user = await fetchAuthSession();
                    // Determine the identity provider
                    const provider = user.tokens.idToken.payload['identities']?.[0]?.providerName || 'Cognito';

                    // here is your name, email e.t.c.
                    const { email } = user.tokens.idToken.payload;
                    if (provider === 'Google') {
                        await loginWithThirdParty(email, data.username);
                    }
                    setTimeout(() => {
                        setLoading(false);
                    }, 2000);

                    break;
                }
                case 'signOut':
                    break;
                case 'customOAuthState':
                    break;
                case 'signInWithRedirect_failure':
                    setLoading(false);
                    break;
                default:
                    break;
            }
        });

        return unsubscribe;
    }, [loginWithThirdParty]);

    useEffect(() => {
        const getCurrentSession = async () => {
            let cachedUser = null;

            let token = localStorage.getItem('auth_token');
            if (auth && location.pathname === '/lti-launch') {
                const { token: ltiLaunchToken } = queryString.parse(location.search);
                localStorage.removeItem('auth_token');
                if (ltiLaunchToken) {
                    localStorage.setItem('auth_token', ltiLaunchToken);
                    token = ltiLaunchToken;
                }
            }
            if (location.pathname === '/accepted-invitation') {
                await signOut({
                    global: false,
                    oauth: {
                        redirects: `https://${platform}.timelygrader.ai`,
                        redirectUrl: `https://${platform}.timelygrader.ai`,
                    },
                });
                const { token: incomingToken, confirmation_code } = queryString.parse(location.search);

                localStorage.setItem('auth_token', incomingToken);
                cachedUser = jwt.decode(incomingToken);
                axios.defaults.headers.common.Authorization = `Bearer ${incomingToken}`;
                try {
                    const userData = await userRequests.getUser(cachedUser.user_id);
                    await verifyUser(userData.username, confirmation_code);

                    if (userData?.finished_sign_up || userData?.profile_set_up) {
                        setLoading(false);
                        message.success('Thank you for accepting the invitation. Please login again.');
                        return logout({ isExpired: false });
                    }
                    setAuth({ ...cachedUser });
                    setCurrentUser(userData);

                    const route = await login({
                        username: userData.username,
                        password: userData.password,
                        confirmation_code,
                    });
                    if (route === 'profile-set-up') {
                        history.push('/account-settings');
                    }
                } catch (e) {
                    setLoading(false);
                    console.log(e);
                    return logout({ isExpired: false });
                }
            } else if (token) {
                cachedUser = jwt.decode(token);
                axios.defaults.headers.common.Authorization = `Bearer ${token}`;
                if (cachedUser?.owner_account_id) {
                    const accountData = await accountRequests.findAccount(cachedUser.owner_account_id);
                    if (accountData?.active === false) return await logout({ isExpired: true });
                    setAuth({ ...cachedUser, ...accountData });
                    const userData = await userRequests.getUser(cachedUser.user_id);
                    const userStatus = await getCurrentUserStatus();
                    setCurrentUser(userData);

                    if (accountData?.course_id) {
                        const existingCourseData = await courseRequests.findCourse(accountData?.course_id);

                        if (
                            cachedUser.role === 'instructor' &&
                            !existingCourseData?.finished_course_set_up &&
                            existingCourseData?.created_on > new Date('2021-12-01').getTime()
                        ) {
                            history.push('/course-set-up');
                        }
                        if (userData && cachedUser?.role && cachedUser.role !== 'admin' && !userData.profile_set_up) {
                            if (!userStatus) {
                                history.push('/account-settings');
                            } else {
                                history.push('/profile-setup');
                            }
                        }
                    }
                } else {
                    const userData = await userRequests.getUser(cachedUser.user_id);
                    const userStatus = await getCurrentUserStatus();
                    setCurrentUser(userData);
                    setAuth({ ...cachedUser, ...userData });
                    if (userData && !userData.profile_set_up) {
                        if (!userStatus) {
                            history.push('/account-settings');
                        } else {
                            history.push('/profile-setup');
                        }
                    }
                }
                try {
                    const session = await fetchAuthSession();
                    if (session?.idToken?.payload?.['cognito:username']?.includes('google')) {
                        setAuth((current) => ({ ...current, is_third_party: true }));
                        setCurrentUser((current) => ({ ...current, is_third_party: true }));
                    }
                    setLoading(false);
                } catch (e) {
                    // eslint-disable-next-line no-console
                    console.error(e);
                    return logout({ isExpired: true });
                }
            } else {
                setAuth(null);
                setLoading(false);
            }
        };

        if (!auth) {
            getCurrentSession();
        }
    }, [auth, history, location.pathname, location.search, login, logout, message, verifyUser]);

    return (
        <AuthContext.Provider
            value={{
                loading,
                auth,
                currentUser,
                setAuth,
                isLTI: false,
                getCurrentUser,
                setCurrentUser,
                updateCurrentUser,
                updateNotificationPreferences,
                updateProfile,
                login,
                loginWithThirdParty,
                logout,
                forgotPassword,
                updatePassword,
                selectAccount,
                changeAccount,
                confirmForgotPassword,
                resendConfirmationCode,
                register,
                verifyUser,
                sessionExpired,
                startCourseSetup,
                confirmCourseSetUp,
                signInWithGoogle,
            }}
            {...props}
        />
    );
};

export { useAuth };
export default AuthProvider;
